"use client";

import { useEffect, useMemo, useState } from "react";
import { CalendarOff } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  announcedClosures,
  formatClosureWindow,
  normalizeClosurePolicy,
  resumeDate,
  sanitiseClosureReason,
  type ClosurePeriod,
  type ClosurePolicy,
} from "@/lib/closures";
import { useClosureNoticeSession } from "@/stores/closure-notice-store";
import { useCookieConsent } from "@/stores/cookie-consent-store";

/**
 * The closure bar's MARKUP, plus a client-side refresh.
 *
 * The refresh exists because 26 storefront pages are statically prerendered
 * (`/privacy`, `/terms`, `/zh`, every SEO landing page …). Those bake the bar at
 * BUILD time, so a closure configured afterwards never appeared on them, and a
 * closure that had ended kept showing until the next deploy. Production looked
 * correct only because the last deploy happened to follow the last config
 * change — luck, not correctness.
 *
 * This is the same self-healing shape `AnnouncementBar` already uses: the server
 * renders what it knows, and the client corrects it. The first client render
 * MUST match the server's, so the fetched value is adopted in an effect and
 * never during render — otherwise a static page hydrates with a mismatch.
 *
 * `HeaderBanners` measures the banner stack with a ResizeObserver, so a bar that
 * appears or disappears after this fetch updates `--banner-h` correctly and the
 * page reflows rather than overlapping.
 */
export function ClosureNoticeClient({
  initialClosures,
  initialPolicy,
}: {
  /** Announced closures as of the server render (possibly stale on a baked page). */
  initialClosures: ClosurePeriod[];
  initialPolicy: ClosurePolicy;
}) {
  const [closures, setClosures] = useState(initialClosures);
  const [policy, setPolicy] = useState(initialPolicy);
  const [dialogOpen, setDialogOpen] = useState(false);
  const dialogSeenKey = useClosureNoticeSession((s) => s.dialogSeenKey);
  const markDialogSeen = useClosureNoticeSession((s) => s.markDialogSeen);
  const cookieConsent = useCookieConsent((s) => s.consent);
  const cookieConsentMounted = useCookieConsent((s) => s.mounted);
  const closureKey = useMemo(() => closureNoticeKey(closures, policy), [closures, policy]);

  useEffect(() => {
    let cancelled = false;
    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), 8000);

    fetch("/api/products/settings", { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((data: unknown) => {
        if (cancelled || !data || typeof data !== "object") return;
        const s = data as { closures?: unknown; closurePolicy?: unknown };
        // The public endpoint already serves ANNOUNCED closures only, sanitised.
        // Re-running `announcedClosures` is deliberate and idempotent: it also
        // re-evaluates the window against the CLIENT's clock, which is what
        // retires a finished closure on a page left open for hours.
        const nextPolicy = normalizeClosurePolicy(s.closurePolicy);
        const nextClosures = announcedClosures(
          s.closures as ClosurePeriod[] | undefined,
          nextPolicy,
          new Date()
        );
        setPolicy(nextPolicy);
        setClosures(nextClosures);
      })
      .catch(() => {
        // A closure notice is helpful, never load-bearing. On any failure keep
        // whatever the server rendered rather than blanking a live warning.
      })
      .finally(() => clearTimeout(timer));

    return () => {
      cancelled = true;
      ctrl.abort();
      clearTimeout(timer);
    };
  }, []);

  // This deliberately runs only after the matching server/client first render.
  // The persisted session value is browser-only; opening during render would
  // turn a static page's hydration into a mismatched dialog tree.
  useEffect(() => {
    // First-time visitors must be able to make their cookie choice before a
    // modal captures focus. Once consent is decided, this notice opens next.
    if (
      !cookieConsentMounted ||
      cookieConsent === null ||
      !closureKey ||
      dialogSeenKey === closureKey
    ) return;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- once-per-session dialog is intentionally triggered after hydration.
    setDialogOpen(true);
    markDialogSeen(closureKey);
  }, [closureKey, cookieConsent, cookieConsentMounted, dialogSeenKey, markDialogSeen]);

  const showDetails = () => {
    setDialogOpen(true);
    // A visitor who deliberately opens the notice before choosing cookies has
    // already seen it; do not interrupt them with the same dialog afterwards.
    if (closureKey) markDialogSeen(closureKey);
  };

  if (closures.length === 0) return null;

  return (
    <>
      <div
        role="status"
        aria-label="Closure notice"
        data-testid="closure-notice-bar"
      // Semantic warning tokens, NOT raw Tailwind amber. `--warning-soft` and
      // friends are the house warning style (globals.css) and are already
      // redefined per theme, so no theme-variant class belongs here — the
      // storefront is light-locked anyway (lib/theme.ts).
        className="border-b border-warning-border bg-warning-soft px-6 py-2 text-warning-soft-foreground md:px-10 lg:px-16"
      >
        <div className="mx-auto grid max-w-7xl grid-cols-[auto_minmax(0,1fr)] items-start gap-x-2 gap-y-1 sm:grid-cols-[auto_minmax(0,1fr)_auto]">
          <CalendarOff className="mt-0.5 h-4 w-4 shrink-0 text-warning" aria-hidden="true" />
          <div className="min-w-0 flex-1 space-y-0.5 text-sm leading-snug">
            {closures.map((c) => (
              <p key={c.id}>{closureBannerLine(c, policy)}</p>
            ))}
          </div>
          <button
            type="button"
            onClick={showDetails}
            className="col-start-2 inline-flex min-h-11 shrink-0 items-center justify-self-start rounded-lg border border-warning-border bg-background/60 px-3 text-sm font-medium underline underline-offset-2 transition-colors hover:bg-background focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-warning focus-visible:ring-offset-2 sm:col-start-3 sm:row-start-1 sm:-my-2"
          >
            View details
          </button>
        </div>
      </div>
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-h-[calc(100dvh-1rem)] max-w-[calc(100%-1rem)] gap-0 overflow-y-auto overscroll-contain rounded-2xl border-0 bg-background p-0 shadow-2xl sm:max-w-lg sm:rounded-3xl">
          <DialogHeader className="bg-linear-to-b from-warning-soft to-background px-6 pb-5 pt-9 text-center sm:px-8">
            <div className="mx-auto mb-4 h-px w-10 bg-primary" aria-hidden="true" />
            <p className="text-[10px] font-medium uppercase tracking-[0.22em] text-(--stone)">
              The Floral Muse
            </p>
            <DialogTitle className="mt-2 font-serif text-2xl font-semibold leading-tight sm:text-3xl">
              Holiday &amp; closure dates
            </DialogTitle>
            <DialogDescription className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Dates during the break are unavailable, but you can choose any available date after we reopen.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 px-6 pb-7 pt-2 sm:px-8 sm:pb-8">
            <div className="space-y-3">
              {closures.map((closure) => {
                const reason = sanitiseClosureReason(closure.reason);
                const resume = shortDate(resumeDate(closure, policy, []));
                return (
                  <article
                    key={closure.id}
                    className="grid grid-cols-[auto_1fr] gap-3 rounded-2xl border border-warning-border bg-warning-soft p-4"
                  >
                    <span className="flex size-10 items-center justify-center rounded-full bg-background text-warning">
                      <CalendarOff className="size-4.5" aria-hidden="true" />
                    </span>
                    <div className="min-w-0 break-words">
                      <p className="font-serif text-lg font-semibold leading-snug text-foreground">
                        {formatClosureWindow(closure) || "Closure dates"}
                      </p>
                      {reason ? <p className="mt-1 text-sm text-muted-foreground">{reason}</p> : null}
                      {resume ? (
                        <p className="mt-2 text-sm font-semibold text-warning-soft-foreground">
                          Orders resume {resume}.
                        </p>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
            <Button
              type="button"
              size="lg"
              onClick={() => setDialogOpen(false)}
              className="mt-5 w-full rounded-xl sm:w-auto"
            >
              Continue browsing
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** Stable, data-derived key: different closure copy or lead policy reopens it. */
export function closureNoticeKey(closures: readonly ClosurePeriod[], policy: ClosurePolicy): string {
  if (closures.length === 0) return "";
  return JSON.stringify({
    closures: closures.map(({ id, start, end, reason, noticeDaysBefore, resumeDays }) => ({
      id,
      start,
      end,
      reason: sanitiseClosureReason(reason),
      noticeDaysBefore,
      resumeDays,
    })),
    policy,
  });
}

/**
 * "We're closed 24 Dec 5pm – 2 Jan 9am (Christmas) — orders resume 4 Jan."
 *
 * Deliberately NOT `closureWarning()`: that sentence ends "your order will be
 * prepared on the date you choose", which is only true where a date is being
 * chosen. On a browse page it would be a non-sequitur.
 */
function closureBannerLine(c: ClosurePeriod, policy: ClosurePolicy): string {
  const when = formatClosureWindow(c);
  const reason = sanitiseClosureReason(c.reason);
  // Empty `lines` = the conservative longest lead across every product line.
  const resume = shortDate(resumeDate(c, policy, []));
  const head = when ? `We're closed ${when}` : "We're closed";
  const withReason = reason ? `${head} (${reason})` : head;
  return resume ? `${withReason} — orders resume ${resume}.` : `${withReason}.`;
}

/** "4 Jan" from a `YYYY-MM-DD` key; empty string if the key is unusable. */
function shortDate(key: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(key);
  if (!m) return "";
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}
